import React, { useEffect, useState, useRef } from 'react';
import { Card, Container, Row, Col, Form, Button, Spinner, Badge, Accordion } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

const StudyHelper = () => {
  const userData = sessionStorage.getItem('user');
  const userObj = userData ? JSON.parse(userData) : {};
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const initialForm = { board_name: '', subject_id: '', class_id: '' };

  const [form, setForm] = useState(initialForm);
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [boards, setBoards] = useState([]);

  // Loading flags
  const [boardLoading, setBoardLoading] = useState(false);
  const [classesLoading, setClassesLoading] = useState(false);
  const [subjectsLoading, setSubjectsLoading] = useState(false);

  // Resolved board (from school_id)
  const [board, setBoard] = useState({ id: '', name: '' });

  // Active action for buffering state ('ingest', 'answer', 'ask')
  const [activeAction, setActiveAction] = useState(null);

  // Question validation limits
  const [questionError, setQuestionError] = useState('');
  const MAX_CHAR_LIMIT = 1000;

  const sending = !!activeAction;

  // Composer state
  const [files, setFiles] = useState([]);
  const [question, setQuestion] = useState('');

  // Display result
  const [singleAnswer, setSingleAnswer] = useState('');
  const [status, setStatus] = useState('');
  const [tips, setTips] = useState([]);
  const [citations, setCitations] = useState([]);
  const [multiResults, setMultiResults] = useState([]);
  const [singleMapData, setSingleMapData] = useState(null);

  const fileInputRef = useRef(null);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setForm((prev) => {
      if (id === 'class_id') return { ...prev, class_id: value, subject_id: '' };
      return { ...prev, [id]: value };
    });
  };

  // CLEAR / RESET: keep resolved board name, clear everything else and hide answer
  const handleReset = () => {
    setForm({ ...initialForm, board_name: board.name });
    setFiles([]);
    setQuestion('');
    setQuestionError('');
    setSingleAnswer('');
    setStatus('');
    setTips([]);
    setCitations([]);
    setMultiResults([]);
    setSingleMapData(null);
    toast.info('Cleared');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.board_name || !form.class_id || !form.subject_id) {
      toast.error('Please fill all required fields');
      return;
    }
    toast.success('Submitted!');
  };

  // Load boards and default resolved board via school_id
  useEffect(() => {
    let cancelled = false;

    const loadBoardsData = async () => {
      if (!userObj?.school_id || !baseUrl) return;
      try {
        setBoardLoading(true);
        // Load all boards list for dropdown
        const boardsRes = await axios.post(`${baseUrl}/board/`, {
          action: 'READ',
          school_id: userObj.school_id,
        });
        if (!cancelled) {
          setBoards(Array.isArray(boardsRes.data) ? boardsRes.data : []);
        }

        // Load resolved default board
        const params = { school_id: userObj.school_id };
        const { data } = await axios.get(`${baseUrl}/getboard/`, { params });

        const id = data?.board_id ?? data?.id ?? '';
        const name = data?.board_name ?? data?.name ?? '';

        if (!cancelled) {
          setBoard({ id, name });
          setForm((prev) => ({ ...prev, board_name: prev.board_name || name }));
        }
      } catch (err) {
        console.error('Error loading boards:', err);
        toast.error('Failed to load board details');
        if (!cancelled) {
          setBoards([]);
          setBoard({ id: '', name: '' });
        }
      } finally {
        if (!cancelled) setBoardLoading(false);
      }
    };

    loadBoardsData();
    return () => { cancelled = true; };
  }, [baseUrl, userObj.school_id]);

  // FORCE SCROLL FIX: Ensure parent .pageBody allows vertical scrolling
  useEffect(() => {
    const pageBody = document.querySelector('.pageBody');
    if (pageBody) {
      pageBody.style.overflowY = 'auto';
      pageBody.style.height = 'auto';
      pageBody.style.display = 'block';
    }
    return () => {
      if (pageBody) {
        pageBody.style.overflowY = 'hidden';
      }
    };
  }, []);

  // Load classes
  useEffect(() => {
    let cancelled = false;

    const loadClasses = async () => {
      if (!userObj?.school_id || !baseUrl) return;
      try {
        setClassesLoading(true);
        const params = { school_id: userObj.school_id };
        const classesRes = await axios.get(`${baseUrl}/getclasses/`, { params });
        if (!cancelled) setClasses(Array.isArray(classesRes.data) ? classesRes.data : []);
      } catch (err) {
        console.error('Error loading classes:', err);
        toast.error('Failed to load classes');
        if (!cancelled) setClasses([]);
      } finally {
        if (!cancelled) setClassesLoading(false);
      }
    };

    loadClasses();
    return () => { cancelled = true; };
  }, [baseUrl, userObj.school_id]);

  // Load subjects when class changes
  useEffect(() => {
    let cancelled = false;

    const loadSubjects = async () => {
      if (!userObj?.school_id || !baseUrl) return;
      if (!form.class_id) {
        setSubjects([]);
        return;
      }
      try {
        setSubjectsLoading(true);
        const params = {
          school_id: userObj.school_id,
          class_id: form.class_id || undefined,
        };
        const subjectsRes = await axios.get(`${baseUrl}/getsubjects/`, { params });
        if (!cancelled) setSubjects(Array.isArray(subjectsRes.data) ? subjectsRes.data : []);
      } catch (err) {
        console.error('Error loading subjects:', err);
        toast.error('Failed to load subjects');
        if (!cancelled) setSubjects([]);
      } finally {
        if (!cancelled) setSubjectsLoading(false);
      }
    };

    loadSubjects();
    return () => { cancelled = true; };
  }, [baseUrl, userObj.school_id, form.class_id]);

  const selectedClassName = () => {
    const found = classes.find((c) => String(c.class_id) === String(form.class_id));
    return found?.class_name || '';
  };
  const selectedSubjectName = () => {
    const found = subjects.find((s) => String(s.subject_id) === String(form.subject_id));
    return found?.subject_name || '';
  };

  const isPdf = (f) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name);
  const isImage = (f) => /^image\//.test(f.type) || /\.(png|jpe?g|gif|bmp|webp)$/i.test(f.name);

  const onAttachClick = () => fileInputRef.current?.click();
  const onFilesSelected = (e) => {
    const picked = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...picked]);
    e.target.value = null;
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const finalizeAnswer = (text) => {
    if (!text) return '';
    // Normalize bullet char into markdown list char
    let cleaned = text.replace(/^• /gm, '- ').trim();

    // Rename "Support:" to a more meaningful "Explanation:"
    cleaned = cleaned.replace(/\*\*Support:\*\*/gi, '**Explanation:**');
    cleaned = cleaned.replace(/^Support:/gmi, 'Explanation:');

    // Remove leading "Answer:" or "**Answer:**" or "to the question:" to avoid duplicating the UI label
    cleaned = cleaned.replace(/^(?:\*\*)?Answer(?:s)?\s*(?:to\s+the\s+question)?:?(?:\*\*)?\s*/i, '');
    cleaned = cleaned.replace(/^to\s+the\s+question:?\s*/i, '');

    // Strip trailing headers if no content follows them
    const headers = ['Key Terms:', 'Laws:', 'Examples:', 'Explanation:', 'Definition:'];
    headers.forEach(h => {
      if (cleaned.endsWith(h)) {
        cleaned = cleaned.substring(0, cleaned.length - h.length).trim();
      }
    });
    return cleaned;
  };

  const getFullImageUrl = (imageUrl) => {
    if (!imageUrl) return null;

    if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
      return imageUrl;
    }

    const backendBaseUrl = baseUrl || "http://127.0.0.1:5000";
    return `${backendBaseUrl}${imageUrl}`;
  };

  const getFirstPdf = () => files.find(f => isPdf(f));

  const handleIngest = async () => {
    const pdfFile = getFirstPdf();
    if (!pdfFile) {
      toast.error('Please attach a PDF first');
      return;
    }
    const klass = selectedClassName();
    const subject = selectedSubjectName();
    if (!form.board_name || !klass || !subject) {
      toast.error('Select board, class, subject first.');
      return;
    }

    try {
      setActiveAction('ingest');
      setStatus('');
      setSingleAnswer('');
      setMultiResults([]);
      setCitations([]);
      setTips([]);
      setSingleMapData(null);

      const fd = new FormData();
      fd.append('files', pdfFile);
      fd.append('board_id', String(board.id || ''));
      fd.append('class_id', String(form.class_id || ''));
      fd.append('subject_id', String(form.subject_id || ''));
      fd.append('board_name', form.board_name || board.name || '');

      await axios.post(`${baseUrl}/studyhelper/ingest`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('PDF ingested successfully');
    } catch (err) {
      console.error('Ingest error:', err);
      const msg = err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Failed to ingest PDF';
      toast.error(msg);
      setStatus('ERROR');
      setSingleAnswer(msg);
    } finally {
      setActiveAction(null);
    }
  };

  const handleAnswerFromPdf = async () => {
    const pdfFile = getFirstPdf();
    if (!pdfFile) {
      if (!question.trim() && !files.some(f => isImage(f))) {
        toast.error('Please enter a question');
        return;
      }
      return handleSend();
    }
    const klass = selectedClassName();
    const subject = selectedSubjectName();
    if (!form.board_name || !klass || !subject) {
      toast.error('Select board, class, subject first.');
      return;
    }

    try {
      setActiveAction('answer');
      setStatus('');
      setSingleAnswer('');
      setMultiResults([]);
      setCitations([]);
      setTips([]);
      setSingleMapData(null);

      const fd = new FormData();
      fd.append('pdf_file', pdfFile);
      fd.append('board_id', String(board.id || ''));
      fd.append('class_id', String(form.class_id || ''));
      fd.append('subject_id', String(form.subject_id || ''));
      fd.append('max_questions', '10');

      setSingleAnswer("Analyzing PDF questions...");
      const res = await axios.post(`${baseUrl}/studyhelper/answer_from_pdf`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const data = res.data;
      if (data.items) {
        setMultiResults(data.items.map(it => ({ ...it, answer: finalizeAnswer(it.answer) })));
        setSingleAnswer('');
        setSingleMapData(null);
      } else {
        setSingleAnswer(finalizeAnswer(data.answer || data.message || ''));
        setMultiResults([]);
        if (data.answer_type === 'map') {
          setSingleMapData(data);
        } else {
          setSingleMapData(null);
        }
      }
      setStatus(data.status || 'OK');
    } catch (err) {
      console.error('PDF Answer error:', err);
      const msg = err?.response?.data?.detail || err?.message || "Something went wrong while processing the PDF.";
      setStatus('ERROR');
      setSingleAnswer(msg);
      toast.error('Failed to process PDF request');
    } finally {
      setActiveAction(null);
    }
  };

  const handleSend = async () => {
    const klass = selectedClassName();
    const subject = selectedSubjectName();
    if (!form.board_name || !klass || !subject) {
      toast.error('Select board, class, subject first.');
      return;
    }

    if (!question.trim() && !files.some(f => isImage(f))) {
      toast.error("Enter a question or attach an image first.");
      return;
    }

    if (question.length > MAX_CHAR_LIMIT) {
      toast.error(`Question exceeds the maximum limit of ${MAX_CHAR_LIMIT} characters.`);
      return;
    }

    try {
      setActiveAction('ask');
      setStatus('');
      setSingleAnswer('Loading...');
      setMultiResults([]);
      setCitations([]);
      setTips([]);
      setSingleMapData(null);

      const imageFile = files.find(f => isImage(f));
      const fd = new FormData();
      if (question.trim()) fd.append('question', question.trim());
      if (imageFile) fd.append('image_file', imageFile);

      fd.append('board_id', String(board.id || ''));
      fd.append('class_id', String(form.class_id || ''));
      fd.append('subject_id', String(form.subject_id || ''));

      const res = await axios.post(`${baseUrl}/studyhelper/ask_question`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const data = res.data;
      if (data.items) {
        setMultiResults(data.items.map(it => ({ ...it, answer: finalizeAnswer(it.answer) })));
        setSingleAnswer('');
        setSingleMapData(null);
      } else {
        setSingleAnswer(finalizeAnswer(data.answer || data.message || ''));
        setMultiResults([]);
        if (data.answer_type === 'map') {
          setSingleMapData(data);
        } else {
          setSingleMapData(null);
        }
      }
      setStatus(data.status || 'OK');
      setTips(data.tips || []);
      setCitations(data.citations || []);
    } catch (err) {
      console.error('Send error:', err);
      const msg = err?.response?.data?.detail || err?.message || "Something went wrong.";
      setStatus('ERROR');
      setSingleAnswer(msg);
      setTips([]);
      toast.error('Failed to process request');
    } finally {
      setActiveAction(null);
    }
  };

  return (
    <div className="pageBody">
      <ToastContainer />
      <Container fluid>
        <Card>
          <Card.Body>
            <form onSubmit={handleSubmit}>
              <Row>
                <Col xs={12}>
                  <h6 className="commonSectionTitle">Study Helper</h6>
                </Col>
              </Row>

              <Row>
                {/* Board */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Board <span className="requiredStar">*</span></Form.Label>
                      <Form.Select
                        required
                        id="board_name"
                        value={form.board_name}
                        onChange={(e) => {
                          const val = e.target.value;
                          const selected = boards.find(b => b.board_name === val);
                          setForm(prev => ({ ...prev, board_name: val }));
                          if (selected) {
                            setBoard({ id: selected.board_id, name: selected.board_name });
                          }
                        }}
                        disabled={boardLoading || !!board.name}
                      >
                        <option value="">{boardLoading ? 'Loading…' : 'Select Board'}</option>
                        {boards.map((b) => (
                          <option key={b.board_id} value={b.board_name}>
                            {b.board_name}
                          </option>
                        ))}
                      </Form.Select>
                      <input type="hidden" name="board_id" value={board.id} />
                    </Form.Group>
                  </div>
                </Col>

                {/* Class */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Class <span className="requiredStar">*</span></Form.Label>
                      <Form.Select
                        required
                        id="class_id"
                        value={form.class_id}
                        onChange={handleInputChange}
                        disabled={classesLoading}
                      >
                        <option value="">{classesLoading ? 'Loading…' : 'Select Class'}</option>
                        {classes.map((c) => (
                          <option key={c.class_id} value={c.class_id}>{c.class_name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </Col>

                {/* Subject */}
                <Col xs={12} md={6} lg={4} xxl={3}>
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Subject <span className="requiredStar">*</span></Form.Label>
                      <Form.Select
                        required
                        id="subject_id"
                        value={form.subject_id}
                        onChange={handleInputChange}
                        disabled={subjectsLoading || !form.class_id || subjects.length === 0}
                      >
                        <option value="">
                          {!form.class_id ? 'Select Class first' : (subjectsLoading ? 'Loading…' : (subjects.length ? 'Select Subject' : 'No subjects'))}
                        </option>
                        {subjects.map((s) => (
                          <option key={s.subject_id} value={s.subject_id}>{s.subject_name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </Col>
              </Row>

              {/* Question + Actions */}
              <Row className="mt-4">
                <Col xs={12} md={10} lg={8}>
                  {/* File badges */}
                  {files.length > 0 && (
                    <div className="mb-2">
                      {files.map((f, idx) => (
                        <Badge bg="secondary" key={idx} className="me-2">
                          {f.name}
                          <Button
                            size="sm"
                            variant="link"
                            onClick={() => removeFile(idx)}
                            className="text-white ms-1 p-0"
                            aria-label={`Remove ${f.name}`}
                          >
                            ✕
                          </Button>
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* Composer */}
                  <div className="d-flex align-items-center gap-2">
                    <div className="flex-grow-1">
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={question}
                        maxLength={MAX_CHAR_LIMIT}
                        onChange={(e) => {
                          const val = e.target.value;
                          setQuestion(val);
                          if (val.length > MAX_CHAR_LIMIT) {
                            setQuestionError(`Question exceeds the maximum limit of ${MAX_CHAR_LIMIT} characters.`);
                          } else {
                            setQuestionError('');
                          }
                        }}
                        placeholder="Ask a question… or attach a file and press Send"
                      />
                      <div className="d-flex justify-content-between align-items-center mt-1">
                        <span className="text-danger small">{questionError}</span>
                        <span className="text-muted small">{question.length} / {MAX_CHAR_LIMIT} characters</span>
                      </div>
                    </div>

                    <div className="d-flex flex-row gap-2 align-self-start flex-shrink-0 mt-1">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf,image/*"
                        multiple
                        className="d-none"
                        onChange={onFilesSelected}
                      />
                      <Button type="button" variant="outline-secondary" onClick={onAttachClick} className="w-auto" disabled={sending}>
                        Attach
                      </Button>
                      <Button type="button" variant="info" onClick={handleIngest} disabled={sending} className="w-auto">
                        {activeAction === 'ingest' ? (<><Spinner size="sm" />&nbsp;Ingesting…</>) : 'Ingest'}
                      </Button>
                      <Button type="button" variant="success" onClick={handleAnswerFromPdf} disabled={sending} className="w-auto">
                        {(activeAction === 'answer' || activeAction === 'ask') ? (<><Spinner size="sm" />&nbsp;Answering…</>) : 'Answer'}
                      </Button>
                      <Button type="button" variant="outline-danger" onClick={handleReset} className="w-auto" disabled={sending}>
                        Clear
                      </Button>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Result Display */}
              {(!!singleAnswer || citations.length > 0 || !!status || multiResults.length > 0) && (
                <Row className="mt-3">
                  <Col xs={12} md={10} lg={8}>
                    <Card border={status && status !== 'OK' ? 'warning' : 'light'}>
                      <Card.Body>
                        {multiResults.length > 0 ? (
                          /* Multi-Question Results Rendering */
                          <div className="multiResultsWrap">
                            <h6 className="mb-3 d-flex align-items-center gap-2">
                              <Badge bg="primary">Results ({multiResults.length})</Badge>
                            </h6>
                            <Accordion defaultActiveKey="0" className="study-accordion">
                              {multiResults.map((item, idx) => (
                                <Accordion.Item eventKey={String(idx)} key={idx} className="mb-2 border rounded shadow-sm">
                                  <Accordion.Header>
                                    <div className="fw-bold" style={{ fontSize: '0.95rem', color: '#2c3e50' }}>
                                      <span className="text-primary me-2">Q{idx + 1}.</span> {item.question}
                                    </div>
                                  </Accordion.Header>
                                  <Accordion.Body className="bg-white">
                                    {item.status === 'OK' ? (
                                      <div className="answerBody">
                                        <ReactMarkdown>{item.answer || '—'}</ReactMarkdown>
                                      </div>
                                    ) : (
                                      <div className="p-2 rounded bg-light border-start border-warning border-4">
                                        <div className="text-dark fw-bold mb-1">Message:</div>
                                        <div className="text-muted">{item.message || 'No specific answer found.'}</div>
                                        {item.tips && item.tips.length > 0 && (
                                          <div className="mt-2 pt-2 border-top">
                                            <div className="small fw-bold text-secondary mb-1">Tips:</div>
                                            <ul className="mb-0 ps-3 small text-muted">
                                              {item.tips.map((tip, t) => <li key={t}>{tip}</li>)}
                                            </ul>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </Accordion.Body>
                                </Accordion.Item>
                              ))}
                            </Accordion>
                          </div>
                        ) : (
                          /* Single Result Rendering */
                          <>
                            {!!singleAnswer && (
                              <>
                                <div className="mb-1">
                                  <strong>{status && status !== 'OK' ? 'System Message:' : 'Answer:'}</strong>
                                </div>
                                <div className="answerBox">
                                  <ReactMarkdown>{singleAnswer || '—'}</ReactMarkdown>
                                </div>
                                {singleMapData && singleMapData.answer_type === "map" && singleMapData.image_url && (
                                  <div className="map-answer-container">
                                    <img
                                      src={getFullImageUrl(singleMapData.image_url)}
                                      alt={`${singleMapData.map_region || "Map"} showing ${singleMapData.target_location || "location"}`}
                                      className="map-answer-image"
                                    />
                                  </div>
                                )}
                                {singleMapData && singleMapData.answer_type === "map" && !singleMapData.image_url && singleMapData.message && (
                                  <div className="map-message">
                                    {singleMapData.message}
                                  </div>
                                )}
                              </>
                            )}

                            {tips && tips.length > 0 && (
                              <div className="mt-3">
                                <h6 className="text-muted" style={{ fontSize: '0.9rem' }}>Suggestions:</h6>
                                <ul className="mb-0" style={{ fontSize: '0.9rem' }}>
                                  {tips.map((tip, i) => <li key={i}>{tip}</li>)}
                                </ul>
                              </div>
                            )}

                            {citations.length > 0 && (
                              <div className="mt-3">
                                <strong>References:</strong>
                                <ul className="mb-0">
                                  {citations.map((c, idx) => (
                                    <li key={idx}>
                                      {c.source || 'Document'} — page {c.page}, chunk {c.chunk_index} (sim {c.similarity})
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}
            </form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default StudyHelper;
